import { useUpdateEffect } from 'react-use';

import {
   HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledQuery,
   HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledSetCommand,
   HealthcareProviderAppointmentConfirmationPatientNotificationConfigurationQuery,
   HealthcareProviderNotificationConfiguration,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult, useCqrsGet } from '@dreryk/command-query-react-common';
import { Translate, useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   Form,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   StyledMargin,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleLoader } from './PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggle.loader';
import { StyledTooltipText } from './PatientPhoneNotificationTooltipText.styled';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;

const useCurrentHealthcareProviderAppointmentCancellationTokenInConfirmationEnabledSetCommand = (): UseCqrsCommandResult<
   void,
   PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleFormBean
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchSnackbarPositiveEvent(
               parameters.notificationEnabled
                  ? patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentCancellationTokenInConfirmationToggleSaveOn
                  : patientPhoneNotificationTranslationKeys.notificationToggle
                       .appointmentCancellationTokenInConfirmationToggleSaveOff,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledQuery);
            refetchCqrsQuery(HealthcareProviderAppointmentConfirmationPatientNotificationConfigurationQuery);
         },
      }
   );
};

type PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleFormBean = {
   notificationEnabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggle = (props: {
   readonly notificationEnabled: boolean;
}): JSX.Element | null => {
   const {
      register,
      handleSubmit,
      reset,
   } = useFormController<PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );

   useUpdateEffect(() => {
      reset({
         notificationEnabled: props.notificationEnabled,
      });
   }, [props.notificationEnabled]);

   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderAppointmentCancellationTokenInConfirmationEnabledSetCommand();

   const {
      data: healthcareProviderNotificationConfiguration,
   } = useCqrsGet<HealthcareProviderNotificationConfiguration>(() => {
      return new HealthcareProviderAppointmentConfirmationPatientNotificationConfigurationQuery();
   });

   return (
      <StyledMargin left={32}>
         <StyledFlex gap={'12px'}>
            <Form onInput={handleSubmit(onSubmit)}>
               <Toggle
                  controller={register('notificationEnabled')}
                  testId={'patientPhoneNotificationAppointmentCancellationTokenInConfirmationToggle'}
                  disabled={
                     loading ||
                     !healthcareProviderNotificationConfiguration ||
                     !healthcareProviderNotificationConfiguration.enabled
                  }
               />
            </Form>
            <StyledFlex gap={'4px'} alignItems={'center'}>
               <Translate>
                  {patientPhoneNotificationTranslationKeys.notificationToggle.appointmentCancellationToggle}
               </Translate>
               <Tooltip
                  maxWidth={'500px'}
                  testId={'appointmentCancellationTokenInConfirmationToggleTooltip'}
                  content={
                     <StyledTooltipText>
                        <Translate>
                           {
                              patientPhoneNotificationTranslationKeys.notificationToggle
                                 .appointmentCancellationTokenInConfirmationToggleTooltip
                           }
                        </Translate>
                     </StyledTooltipText>
                  }
               >
                  <IconWrapper
                     testId={'appointmentCancellationTokenInConfirmationToggleTooltipIconWrapper'}
                     fillColor={IconFillColor.PRIMARY}
                     size={IconSize.SMALL}
                  >
                     <QuestionmarkCircleIcon />
                  </IconWrapper>
               </Tooltip>
            </StyledFlex>
         </StyledFlex>
      </StyledMargin>
   );
};

export const PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleLoader>
         {({ notificationEnabled }) => (
            <PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggle
               notificationEnabled={notificationEnabled}
            />
         )}
      </PatientPhoneNotificationAppointmentCancellationTokenInConfirmationToggleLoader>
   );
};
